import './App.css'
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  Location,
  Router,
  BrowserRouter,
} from 'react-router-dom'
import Homepage from './layouts/Homepage'
import Privatepage from './layouts/PrivatePage'
import Escortpage from './layouts/EscortPage'
import Massagepage from './layouts/MassagePage'
import Header from './layouts/Header'
import DashboardHome from './dashboard/home'
import SettingsPage from './dashboard/settings'
import AnnouncesPage from './dashboard/announces'
import AnnouncesLibertinsPage from './dashboard/announcesLibertins'
import AnnouncesEstablishmentPage from './dashboard/announcesEstablishment'
import AnnouncePage from './dashboard/announce'
import UsersPage from './dashboard/users'
import LoginRegisterPages, { ScreenModeType } from './pages/LoginRegisterPages'
import Footer from './layouts/Footer'
import theme from './components/theme'
import { ThemeProvider } from '@emotion/react'
import ProfilePage from './dashboard/profile'
import { SearchFiltersProvider } from './components/SearchFiltersContext'
import OneAnnounce from './pages/OneAnnounce'
import SearchPage from './layouts/SearchPage'
import EstablishmentPage from './layouts/EstablishmentPage'
import OneEstablishment from './pages/oneEstablishment'
import OneLibertin from './pages/oneLibertin'
import LibertinsPage from './layouts/LibertinsPage'
import OneWork from './pages/oneWork'
import OneEvent from './pages/oneEvent'
import WorkPage from './layouts/WorkPage'
import AnnouncesJobOfferPage from './dashboard/announcesJobOffer'
import AnnouncesEventsPage from './dashboard/announcesEvents'
import JobOffer from './dashboard/jobOffer'
import Event from './dashboard/event'
import EventPage from './layouts/EventPage'
import { useEffect, useState } from 'react'
import AuthService from '../services/login'
import withAuth from '../services/withAuth'
import ResetPasswordPage from './components/ResetPasswordPage'
import RegionsPage from './pages/RegionsPage'
import PopularPage from './pages/PopularPage'
import Support from './dashboard/support'
import HelpPage from './pages/HelpPage'
import ContactPage from './pages/ContactPage'
import StatisticsPage from './dashboard/statistics'
import SearchUserPage from './layouts/SearchUserPage'
import MyGallery from './dashboard/myGallery'
import FavoritesPage from './pages/FavoritesPage'
import CookieManager from './lib/CookiesManager'
import BannerPage from './pages/BannerPage'
import TermsOfUse from './pages/TermsOfUsePage'
import PrivacyPage from './pages/PrivacyPage'
import CookiesPage from './pages/CookiesPage'
import VerifiedStatusPage from './pages/VerifiedStatusPage'
import AsblPage from './pages/AsblPage'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { initGA, pageview } from './lib/gtag'
import ConfirmEmail from './pages/ConfirmEmail'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import BlogPage from './pages/BlogPage'

const PrivateRoute: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(true)

  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      const authStatus = AuthService.isAuthenticated()
      if (!authStatus) {
        try {
          await AuthService.refreshToken()
          await AuthService.updateUserInfo()
          setIsAuthenticated(true)
        } catch (error) {
          setIsAuthenticated(false)
        }
      }
    }

    checkAuth()
    const interval = setInterval(checkAuth, 30 * 60 * 1000)

    return () => clearInterval(interval)
  }, [])

  return isAuthenticated ? <Outlet /> : <Navigate to='/login' />
}

interface LayoutProps {
  children: React.ReactNode
  title: string
  description: string
  location: Location
  showHeader?: boolean
  showFooter?: boolean
}

const Layout: React.FC<LayoutProps> = ({
  children,
  title,
  description,
  location,
  showHeader = true,
  showFooter = true,
}) => {
  const { i18n } = useTranslation()
  const currentLanguage = i18n.language || 'fr-BE'

  useEffect(() => {
    initGA()
  }, [])

  useEffect(() => {
    pageview(location.pathname + location.search)
  }, [location])
  return (
    <>
      <Helmet>
        <html lang={currentLanguage} />
        <title>{title}</title>
        <meta property='og:locale' content={currentLanguage} />
        <meta name='description' content={description} />
        <meta property='og:title' content={title} />
        <meta property='og:description' content={description} />
        <link rel='canonical' href={window.location.href} />
      </Helmet>
      <div lang={currentLanguage}>
        {showHeader && <Header />}
        {children}
        {showFooter && <Footer />}
      </div>
    </>
  )
}

function App() {
  const userId = CookieManager.getUserId() || ''
  const { t, i18n } = useTranslation()
  const helmetContext = {}
  const location = useLocation()
  useEffect(() => {
    const handleLanguageSetup = () => {
      try {
        const hasFunctionalConsent = CookieManager.getConsent().functional
        if (hasFunctionalConsent) {
          const savedLanguage = CookieManager.getLanguage()

          if (savedLanguage && i18n.language !== savedLanguage) {
            i18n.changeLanguage(savedLanguage)
            CookieManager.setLanguage(savedLanguage)
          }
        }
      } catch (error) {
        console.error('Error handling language setup:', error)
      }
    }

    handleLanguageSetup()

    const handleLanguageChange = (newLang: string) => {
      try {
        const hasFunctionalConsent = CookieManager.getConsent().functional
        if (hasFunctionalConsent) {
          CookieManager.setLanguage(newLang)
        }
      } catch (error) {
        console.error('Error handling language change:', error)
      }
    }

    i18n.on('languageChanged', handleLanguageChange)

    return () => {
      i18n.off('languageChanged', handleLanguageChange)
    }
  }, [i18n])
  return (
    <HelmetProvider context={helmetContext}>
      <ThemeProvider theme={theme}>
        <SearchFiltersProvider>
          <Routes>
            <Route
              path='/'
              element={
                <Layout
                  title={t('meta.title')}
                  description={t('meta.description')}
                  location={location}
                >
                  <Homepage />
                </Layout>
              }
            ></Route>
            <Route
              path='/private/'
              element={
                <Layout
                  title={t('meta.private-title')}
                  description={t('meta.private-description')}
                  location={location}
                >
                  <Privatepage />
                </Layout>
              }
            ></Route>
            <Route
              path='/favorites/'
              element={
                <Layout
                  title={t('meta.favorites-title')}
                  description={t('meta.favorites-description')}
                  location={location}
                >
                  <FavoritesPage />
                </Layout>
              }
            ></Route>
            <Route
              path='/escort/'
              element={
                <Layout
                  title={t('meta.escort-title')}
                  description={t('meta.escort-description')}
                  location={location}
                >
                  <Escortpage />
                </Layout>
              }
            ></Route>
            <Route
              path='/escort'
              element={
                <Layout
                  title={t('meta.escort-title')}
                  description={t('meta.escort-description')}
                  location={location}
                >
                  <Escortpage />
                </Layout>
              }
            ></Route>
            <Route
              path='/massage/'
              element={
                <Layout
                  title={t('meta.massage-title')}
                  description={t('meta.massage-description')}
                  location={location}
                >
                  <Massagepage />
                </Layout>
              }
            ></Route>
            <Route
              path='/search/'
              element={
                <Layout
                  title={t('meta.search-title')}
                  description={t('meta.search-description')}
                  location={location}
                >
                  <SearchPage />
                </Layout>
              }
            />
            <Route
              path='/search/user/:id'
              element={
                <>
                  <Header />
                  <SearchUserPage />
                  <Footer />
                </>
              }
            ></Route>
            <Route
              path='/establishment/'
              element={
                <Layout
                  title={t('meta.establishment-title')}
                  description={t('meta.establishment-description')}
                  location={location}
                >
                  <EstablishmentPage />
                </Layout>
              }
            />
            <Route
              path='/libertins/'
              element={
                <Layout
                  title={t('meta.libertins-title')}
                  description={t('meta.libertins-description')}
                  location={location}
                >
                  <LibertinsPage />
                </Layout>
              }
            />
            <Route
              path='/work/'
              element={
                <Layout
                  title={t('meta.work-title')}
                  description={t('meta.work-description')}
                  location={location}
                >
                  <WorkPage />
                </Layout>
              }
            ></Route>
            <Route
              path='/event'
              element={
                <Layout
                  title={t('meta.events-title')}
                  description={t('meta.events-description')}
                  location={location}
                >
                  <EventPage />
                </Layout>
              }
            />
            <Route
              path='/event/:title/:id'
              element={
                <Layout
                  title={t('meta.events-title')}
                  description={t('meta.events-description')}
                  location={location}
                >
                  <OneEvent />
                </Layout>
              }
            />
            <Route
              path='/announce/:gender/:statuses/:title/:id'
              element={
                <Layout
                  title={t('meta.announce.title', 'Annonce | Sexagogo.be')}
                  description={t(
                    'meta.announce.description',
                    "Détails de l'annonce. Photos, services proposés et informations de contact.",
                  )}
                  location={location}
                >
                  <OneAnnounce />
                </Layout>
              }
            />
            <Route path='/confirm-email/:token' element={<ConfirmEmail />} />
            <Route
              path='/establishment/:title/:id'
              element={
                <Layout
                  title={t(
                    'meta.establishment.title',
                    'Établissement | Sexagogo.be',
                  )}
                  description={t('meta.establishment.description')}
                  location={location}
                >
                  <OneEstablishment />
                </Layout>
              }
            />
            <Route
              path='/libertin/:title/:id'
              element={
                <Layout
                  title={t(
                    'meta.libertin.title',
                    'Annonce Libertine | Sexagogo.be',
                  )}
                  description={t('meta.libertin.description')}
                  location={location}
                >
                  <OneLibertin />
                </Layout>
              }
            />
            <Route
              path='/work/:title/:id'
              element={
                <Layout
                  title={t('meta.work-title')}
                  description={t('meta.work-description')}
                  location={location}
                >
                  <OneWork />
                </Layout>
              }
            ></Route>
            <Route
              path='/regions/'
              element={
                <Layout
                  title={t(
                    'meta.regions.title',
                    'Annonces par région | Sexagogo.be',
                  )}
                  description={t(
                    'meta.regions.description',
                    'Trouvez des annonces près de chez vous. Recherche par ville et région en Belgique.',
                  )}
                  location={location}
                >
                  <RegionsPage />
                </Layout>
              }
            />
            <Route
              path='/terms-of-use/'
              element={
                <Layout
                  title={t('meta.terms-title')}
                  description={t('meta.terms-description')}
                  location={location}
                >
                  <TermsOfUse />
                </Layout>
              }
            />
            <Route
              path='/privacy/'
              element={
                <Layout
                  title={t('meta.privacy-title')}
                  description={t('meta.privacy-description')}
                  location={location}
                >
                  <PrivacyPage />
                </Layout>
              }
            />
            <Route
              path='/cookies/'
              element={
                <Layout
                  title={t('meta.cookies-title')}
                  description={t('meta.cookies-description')}
                  location={location}
                >
                  <CookiesPage />
                </Layout>
              }
            />
            <Route
              path='/banners/'
              element={
                <Layout
                  title={t('meta.banners-title')}
                  description={t('meta.banners-description')}
                  location={location}
                >
                  <BannerPage />
                </Layout>
              }
            />
            <Route
              path='/asbl/'
              element={
                <Layout
                  title={t('meta.asbl-title')}
                  description={t('meta.asbl-description')}
                  location={location}
                >
                  <AsblPage />
                </Layout>
              }
            />
            <Route
              path='/help/'
              element={
                <Layout
                  title={t('meta.help-title')}
                  description={t('meta.help-description')}
                  location={location}
                >
                  <HelpPage />
                </Layout>
              }
            />
            <Route
              path='/contact/'
              element={
                <Layout
                  title={t('meta.contact-title')}
                  description={t('meta.contact-description')}
                  location={location}
                >
                  <ContactPage />
                </Layout>
              }
            />
            <Route
              path='/popular/'
              element={
                <Layout
                  title={t(
                    'meta.popular.title',
                    'Annonces Populaires | Sexagogo.be',
                  )}
                  description={t(
                    'meta.popular.description',
                    'Découvrez les annonces les plus consultées du moment.',
                  )}
                  location={location}
                >
                  <PopularPage />
                </Layout>
              }
            />
            <Route
              path='/reset-password/:token'
              element={
                <Layout
                  showFooter={false}
                  showHeader={false}
                  title={t(
                    'meta.reset-password.title',
                    'Réinitialisation du mot de passe | Sexagogo.be',
                  )}
                  description={t(
                    'meta.reset-password.description',
                    'Réinitialisez votre mot de passe en toute sécurité sur Sexagogo.be',
                  )}
                  location={location}
                >
                  <ResetPasswordPage />
                </Layout>
              }
            />
            <Route
              path='/login'
              element={
                <Layout
                  showFooter={false}
                  showHeader={false}
                  title={t(
                    'meta.login.title',
                    'Connexion et Inscription | Sexagogo.be',
                  )}
                  description={t(
                    'meta.login.description',
                    'Connectez-vous ou créez un compte pour accéder à tous nos services.',
                  )}
                  location={location}
                >
                  <LoginPage
                    onSwitchMode={function (mode: ScreenModeType): void {
                      throw new Error('Function not implemented.')
                    }}
                  />
                </Layout>
              }
            />
            <Route
              path='/blog/'
              element={
                <Layout
                  title={t(
                    'blog-title',
                    'Quartiers rouges et vie nocturne | Blog Sexagogo.be',
                  )}
                  description={t(
                    'blog-description',
                    'Explorez le Quartier Rouge d’Amsterdam et la Vida Loca de La Jonquera, entre histoire, liberté et divertissement nocturne.',
                  )}
                  location={location}
                >
                  <BlogPage />
                </Layout>
              }
            ></Route>
            <Route
              path='/register'
              element={
                <Layout
                  showFooter={false}
                  showHeader={false}
                  title={t(
                    'meta.login.title',
                    'Connexion et Inscription | Sexagogo.be',
                  )}
                  description={t(
                    'meta.login.description',
                    'Connectez-vous ou créez un compte pour accéder à tous nos services.',
                  )}
                  location={location}
                >
                  <RegisterPage
                    onSwitchMode={function (mode: ScreenModeType): void {
                      throw new Error('Function not implemented.')
                    }}
                  />
                </Layout>
              }
            />
            <Route element={<PrivateRoute />}>
              <Route
                path='/dashboard/'
                element={
                  <Layout
                    showFooter={false}
                    showHeader={false}
                    title={t(
                      'meta.dashboard-title',
                      'Tableau de bord | Sexagogo.be',
                    )}
                    description={t(
                      'meta.dashboard-description',
                      'Gérez vos annonces, consultez vos statistiques et optimisez votre visibilité. Interface intuitive et complète.',
                    )}
                    location={location}
                  >
                    <>{withAuth(DashboardHome)({})}</>
                  </Layout>
                }
              ></Route>
              <Route
                path='/dashboard/announces/'
                element={
                  <Layout
                    showFooter={false}
                    showHeader={false}
                    title={t(
                      'meta.dashboard-announces.title',
                      'Mes Annonces | Tableau de bord Sexagogo.be',
                    )}
                    description={t(
                      'meta.dashboard-announces.description',
                      'Gérez toutes vos annonces actives, modifiez-les et suivez leurs performances.',
                    )}
                    location={location}
                  >
                    <AnnouncesPage />
                  </Layout>
                }
              />

              <Route
                path='/dashboard/verify-status/'
                element={
                  <Layout
                    showFooter={false}
                    showHeader={false}
                    title={t(
                      'meta.verify-status.title',
                      'Vérification du statut | Sexagogo.be',
                    )}
                    description={t(
                      'meta.verify-status.description',
                      'Vérifiez et mettez à jour votre statut de vérification pour plus de visibilité.',
                    )}
                    location={location}
                  >
                    <VerifiedStatusPage />
                  </Layout>
                }
              />

              <Route
                path='/dashboard/announces-libertin/'
                element={
                  <Layout
                    showFooter={false}
                    showHeader={false}
                    title={t(
                      'meta.announces-libertin.title',
                      'Annonces Libertin | Tableau de bord Sexagogo.be',
                    )}
                    description={t(
                      'meta.announces-libertin.description',
                      'Gérez vos annonces libertines, clubs échangistes et événements libertins.',
                    )}
                    location={location}
                  >
                    <AnnouncesLibertinsPage />
                  </Layout>
                }
              />

              <Route
                path='/dashboard/announces-establishment/'
                element={
                  <Layout
                    showFooter={false}
                    showHeader={false}
                    title={t(
                      'meta.announces-establishment.title',
                      'Établissements | Tableau de bord Sexagogo.be',
                    )}
                    description={t(
                      'meta.announces-establishment.description',
                      'Gérez les annonces de votre établissement, bar, club ou salon de massage.',
                    )}
                    location={location}
                  >
                    <AnnouncesEstablishmentPage />
                  </Layout>
                }
              />

              <Route
                path='/dashboard/announce/'
                element={
                  <Layout
                    showFooter={false}
                    showHeader={false}
                    title={t(
                      'meta.announce.title',
                      'Créer une annonce | Sexagogo.be',
                    )}
                    description={t(
                      'meta.announce.description',
                      'Créez une nouvelle annonce personnalisée et attractive pour maximiser votre visibilité.',
                    )}
                    location={location}
                  >
                    <AnnouncePage />
                  </Layout>
                }
              />

              <Route
                path='/dashboard/my-gallery/'
                element={
                  <Layout
                    title={t(
                      'meta.gallery-title',
                      'Galerie Photos | Sexagogo.be',
                    )}
                    showFooter={false}
                    showHeader={false}
                    description={t(
                      'meta.gallery-description',
                      'Personnalisez votre profil, mettez à jour vos informations et gérez vos paramètres en toute simplicité.',
                    )}
                    location={location}
                  >
                    <MyGallery />
                  </Layout>
                }
              ></Route>

              <Route
                path='/dashboard/statistics/'
                element={<StatisticsPage />}
              ></Route>
              <Route
                path='/dashboard/announce-libertin/'
                element={<AnnouncePage />}
              ></Route>
              <Route
                path='/dashboard/job-offers/'
                element={<AnnouncesJobOfferPage />}
              ></Route>
              <Route
                path='/dashboard/events/'
                element={<AnnouncesEventsPage />}
              ></Route>
              <Route
                path='/dashboard/announce-establishment/'
                element={<AnnouncePage />}
              ></Route>
              <Route
                path='/dashboard/announce-work/'
                element={<JobOffer />}
              ></Route>
              <Route path='/dashboard/support/' element={<Support />}></Route>
              <Route
                path='/dashboard/announce-event/'
                element={<Event />}
              ></Route>
              <Route
                path='/dashboard/settings/'
                element={<SettingsPage userId={userId} />}
              ></Route>
              <Route path='/dashboard/users/' element={<UsersPage />}></Route>
              <Route
                path='/dashboard/profile/'
                element={
                  <Layout
                    title={t('meta.profile-title', 'Profil | Sexagogo.be')}
                    showFooter={false}
                    showHeader={false}
                    description={t(
                      'meta.profile-description',
                      'Personnalisez votre profil, mettez à jour vos informations et gérez vos paramètres en toute simplicité.',
                    )}
                    location={location}
                  >
                    <ProfilePage />
                  </Layout>
                }
              ></Route>
            </Route>
          </Routes>
        </SearchFiltersProvider>
      </ThemeProvider>
    </HelmetProvider>
  )
}

export default App
